import { useMutation } from '@tanstack/react-query';
import { api } from '@/api';
import { queryClient } from '@/queries';
import {
  getHrSystemConflictItemRemover,
  getSchedulerShiftItemRemover,
  getSchedulerShiftItemUpdater,
  ccScheduleQueries,
} from '@/queries/resource/schedule';
import {
  DateRangeParams,
  ResetShiftTypeRequestBody,
  SetFixationTypeRequestBody,
  PlanningProcess,
  SetCCShiftTypeRequestBody,
  CCResourceRulesSettings,
  CCRequirementsInputsSettings,
  SetMyCCShiftTypeRequestBody,
  ResetMyShiftTypeRequestBody,
  CCResolveHrSystemConflict,
  CCShift,
} from '@/api/apis/resources/schedule';

const handleCCSchedulePlanningQueryUpdate = (startDayOfMonth: Date, planningData: PlanningProcess) => {
  queryClient.invalidateQueries(ccScheduleQueries.schedule(startDayOfMonth));
  queryClient.setQueryData(ccScheduleQueries.planningProcess(planningData.id).queryKey, planningData);
};

export const useCCScheduleSetShiftTypeOptimisticMutation = (
  dateRange: DateRangeParams,
  processId?: string | null,
  isPlanned?: boolean,
) => {
  const { queryKey } = ccScheduleQueries.shifts(dateRange, isPlanned ? processId : undefined);

  return useMutation({
    mutationFn: (body: SetCCShiftTypeRequestBody) => api.schedule.cc.setCCShiftType(body),
    onMutate: (body) => {
      const prevSnapshot = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, getSchedulerShiftItemUpdater(body));

      return prevSnapshot;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(queryKey, getSchedulerShiftItemUpdater({ ...variables, ...data }));
      queryClient.invalidateQueries(ccScheduleQueries.validation(variables.scheduleId));
    },
    onError: (_e, _p, context) => {
      queryClient.setQueryData(queryKey, context);
    },
  });
};

export const useCCScheduleResetShiftTypeOptimisticMutation = (
  dateRange: DateRangeParams,
  processId?: string | null,
  isPlanned?: boolean,
) => {
  const { queryKey } = ccScheduleQueries.shifts(dateRange, isPlanned ? processId : undefined);

  return useMutation({
    mutationFn: (body: ResetShiftTypeRequestBody) => api.schedule.cc.resetCCShiftType(body),
    onMutate: (body) => {
      const prevSnapshot = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, getSchedulerShiftItemRemover(body));

      return prevSnapshot;
    },
    onSuccess: (_d, variables) => {
      queryClient.invalidateQueries(ccScheduleQueries.validation(variables.scheduleId));
    },
    onError: (_e, _p, context) => {
      queryClient.setQueryData(queryKey, context);
    },
  });
};

export const useMyCCScheduleSetShiftTypeOptimisticMutation = (dateRange: DateRangeParams) => {
  const { queryKey } = ccScheduleQueries.shifts(dateRange);

  return useMutation({
    mutationFn: ({ resourceId, ...body }: SetMyCCShiftTypeRequestBody & { resourceId: string }) =>
      api.schedule.cc.setMyCCShiftType(body),
    onMutate: ({ required, ...body }) => {
      const prevSnapshot = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, getSchedulerShiftItemUpdater(body));

      return prevSnapshot;
    },
    onSuccess: (data, { required, ...variables }) => {
      queryClient.setQueryData(queryKey, getSchedulerShiftItemUpdater({ ...variables, ...data }));
      queryClient.invalidateQueries(ccScheduleQueries.validation(variables.scheduleId));
    },
    onError: (_e, _p, context) => {
      queryClient.setQueryData(queryKey, context);
    },
  });
};

export const useMyCCScheduleResetShiftTypeOptimisticMutation = (dateRange: DateRangeParams) => {
  const { queryKey } = ccScheduleQueries.shifts(dateRange);

  return useMutation({
    mutationFn: ({ resourceId, ...body }: ResetMyShiftTypeRequestBody & { resourceId: string }) =>
      api.schedule.cc.resetMyCCShiftType(body),
    onMutate: (body) => {
      const prevSnapshot = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, getSchedulerShiftItemRemover(body));

      return prevSnapshot;
    },
    onError: (_e, _p, context) => {
      queryClient.setQueryData(queryKey, context);
    },
  });
};

export const useCCCreatePlanningMutation = () => {
  return useMutation({
    mutationFn: (startDayOfMonth: Date) => api.schedule.cc.createCCPlanningProcess(startDayOfMonth),
    onSuccess: (data, startDayOfMonth) => {
      handleCCSchedulePlanningQueryUpdate(startDayOfMonth, data);
    },
  });
};

export const useCCRegeneratePlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: (processId: string) => api.schedule.cc.regenerateCCPlanningProcess(processId),
    onSuccess: (data) => {
      handleCCSchedulePlanningQueryUpdate(startDayOfMonth, data);
    },
  });
};

export const useCCCancelPlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: (processId: string) => api.schedule.cc.cancelCCPlanningProcess(processId),
    onSuccess: (data) => {
      handleCCSchedulePlanningQueryUpdate(startDayOfMonth, data);
    },
  });
};

export const useCCPublishPlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: ({ processId, scheduleId }: { processId: string; scheduleId: string }) =>
      api.schedule.cc.publishCCPlanningProcess(processId, scheduleId),
    onSuccess: (data) => {
      handleCCSchedulePlanningQueryUpdate(startDayOfMonth, data);
    },
  });
};

export const useCCCancelGenerationPlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: ({ processId, jobId }: { processId: string; jobId: string }) =>
      api.schedule.cc.cancelGenerationCCPlanningProcess(processId, jobId),
    onSuccess: (data) => {
      handleCCSchedulePlanningQueryUpdate(startDayOfMonth, data);
    },
  });
};

export const useCCSetScheduleSettingsRulesMutation = () => {
  return useMutation({
    mutationFn: (body: CCResourceRulesSettings) => api.schedule.cc.setCCScheduleRules(body),
    meta: {
      invalidates: [ccScheduleQueries.rules().queryKey],
    },
    onSuccess: (data) => {
      queryClient.setQueryData(ccScheduleQueries.rules().queryKey, data);
    },
  });
};

export const useCCSetScheduleSettingsInputsMutation = () => {
  return useMutation({
    mutationFn: (body: CCRequirementsInputsSettings) => api.schedule.cc.setCCScheduleInputs(body),
    meta: {
      invalidates: [ccScheduleQueries.inputs().queryKey],
    },
    onSuccess: (data) => {
      queryClient.setQueryData(ccScheduleQueries.inputs().queryKey, data);
    },
  });
};

export const useCCScheduleSyncWorkdayMutation = (
  startDayOfMonth: Date,
  dateRange: DateRangeParams,
  processId?: string | null,
  isPlanned?: boolean,
) => {
  return useMutation({
    mutationFn: (scheduleId: string) => api.schedule.cc.ccScheduleRefreshHr(scheduleId),
    onSuccess: (_, scheduleId) => {
      const planningProcessId = isPlanned ? processId : undefined;
      queryClient.invalidateQueries({ queryKey: ccScheduleQueries.shifts(dateRange, planningProcessId).queryKey });
      queryClient.invalidateQueries({ queryKey: ccScheduleQueries.schedule(startDayOfMonth).queryKey });
      queryClient.invalidateQueries({ queryKey: ccScheduleQueries.planningProcess(processId).queryKey });
      queryClient.invalidateQueries({ queryKey: ccScheduleQueries.hrSystemConflicts(scheduleId).queryKey });
    },
  });
};

export const useCCHrSystemConflictResolveMutation = (dateRange: DateRangeParams, scheduleId?: string) => {
  return useMutation({
    mutationFn: (payload: CCResolveHrSystemConflict) => api.schedule.cc.resolveCCScheduleHrSystemConflict(payload),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        ccScheduleQueries.hrSystemConflicts(scheduleId).queryKey,
        getHrSystemConflictItemRemover(variables.conflictId),
      );
      queryClient.setQueryData(ccScheduleQueries.shifts(dateRange).queryKey, getSchedulerShiftItemUpdater(data));
    },
  });
};

export const useCCSetFixationTypeOptimisticMutation = (
  dateRange: DateRangeParams,
  processId?: string | null,
  isPlanned?: boolean,
) => {
  const { queryKey } = ccScheduleQueries.shifts(dateRange, isPlanned ? processId : undefined);

  return useMutation({
    mutationFn: (body: SetFixationTypeRequestBody) => api.schedule.cc.setCCFixationType(body),
    onMutate: (body) => {
      const prevSnapshot = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(
        queryKey,
        getSchedulerShiftItemUpdater({ ...body, fixationType: body.fixationType, isEditable: true } as CCShift),
      );
      return prevSnapshot;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(ccScheduleQueries.validation(variables.scheduleId));
    },
    onError: (_e, _p, context) => {
      queryClient.setQueryData(queryKey, context);
    },
  });
};
